import React, { useState, useEffect, useMemo } from "react";
import { DocRenderer } from "react-doc-viewer";
import { CircularProgress, Typography } from "@mui/material";
import Papa from "papaparse";
import CustomTable, {
  TableColumn,
} from "@/components/common/table/CustomTable";

interface CSVRow {
  [key: string]: string | number;
}

const CSVViewer: DocRenderer = ({ mainState: { currentDocument } }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [csvData, setCsvData] = useState<{
    headers: string[];
    rows: CSVRow[];
  }>({ headers: [], rows: [] });

  useEffect(() => {
    const fetchAndParseCSV = async () => {
      if (!currentDocument?.uri) {
        setError("No document provided.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(currentDocument.uri);
        if (!response.ok) throw new Error("Failed to fetch document.");

        const csvText = await response.text();

        // Parse CSV using PapaParse
        Papa.parse(csvText, {
          complete: (results) => {
            if (results.data && results.data.length > 0) {
              const headers = results.data[0] as string[];
              const rows = results.data.slice(1).map((row: any) => {
                const rowData: CSVRow = {};
                headers.forEach((header, index) => {
                  // Try to convert to number if possible
                  const value = row[index];
                  rowData[header] = !isNaN(Number(value))
                    ? Number(value)
                    : value || "";
                });
                return rowData;
              });

              setCsvData({ headers, rows });
              setError(null);
            } else {
              setError("No data found in CSV file.");
            }
            setLoading(false);
          },
          error: (error: any) => {
            setError(`Error parsing CSV: ${error.message}`);
            setLoading(false);
          },
          header: false,
          skipEmptyLines: true,
          dynamicTyping: true,
        });
      } catch (err) {
        setError("Error loading CSV file.");
        console.error(err);
        setLoading(false);
      }
    };

    fetchAndParseCSV();
  }, [currentDocument?.uri]);

  const columns: TableColumn<CSVRow>[] = useMemo(() => {
    return csvData.headers.map((header) => ({
      header,
      accessor: header,
      sortable: true,
      sortKey: header,
    }));
  }, [csvData.headers]);

  const fetchTableData = async ({
    page,
    limit,
    search,
    sortColumn,
    sortDirection,
  }: {
    page: number;
    limit: number;
    search: string;
    sortColumn: string;
    sortDirection: "asc" | "desc";
  }) => {
    // Filter data based on search
    let filteredData = [...csvData.rows];
    if (search) {
      filteredData = filteredData.filter((row) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(search.toLowerCase()),
        ),
      );
    }

    // Sort data
    if (sortColumn) {
      filteredData.sort((a, b) => {
        const aValue = a[sortColumn];
        const bValue = b[sortColumn];

        // Handle numeric sorting
        if (typeof aValue === "number" && typeof bValue === "number") {
          return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
        }

        // Handle string sorting
        const comparison = String(aValue).localeCompare(String(bValue));
        return sortDirection === "asc" ? comparison : -comparison;
      });
    }

    // Paginate data
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedData = filteredData.slice(startIndex, endIndex);

    return {
      ok: true,
      data: {
        data: paginatedData,
        meta: {
          count: filteredData.length,
          page,
          limit,
        },
      },
    };
  };

  return (
    <div className="tw-p-4 tw-rounded-lg tw-bg-white tw-shadow-md tw-w-full tw-h-full">
      <Typography variant="h6" className="tw-text-xl tw-font-bold tw-mb-4">
        CSV File Preview
      </Typography>

      {loading && (
        <div className="tw-flex tw-justify-center tw-items-center tw-h-40">
          <CircularProgress />
        </div>
      )}

      {error && (
        <Typography className="tw-text-red-500 tw-font-semibold tw-text-center">
          {error}
        </Typography>
      )}

      {!loading && !error && csvData.rows.length > 0 && (
        <div className="tw-border tw-border-t-0 tw-border-gray-100 tw-max-h-[850px] tw-overflow-auto">
          <CustomTable<CSVRow>
            columns={columns}
            fetchData={fetchTableData}
            hideControls={false}
            hidePagination={false}
            removeInitialPadding={true}
            noDataMessage="No data available in the CSV file"
            noSearchDataMessage="No matching records found"
            forceRefresh={false}
            setForceRefresh={() => {}}
            customPaginationDefaultValues={{
              limit: 5,
            }}
          />
        </div>
      )}

      {!loading && !error && csvData.rows.length === 0 && (
        <Typography className="tw-text-gray-500 tw-text-center">
          No data available.
        </Typography>
      )}
    </div>
  );
};

CSVViewer.fileTypes = ["csv"];
CSVViewer.weight = 1;

export default CSVViewer;
